'use client';

import { filterEarlyBirdMenu, isEarlyBirdOnly, type TicketingFestival } from '@/utils/early-bird';
import { FrontendMenuItem, MenuState } from '@/types/menu.types';
import { normalizeBasePath, withBasePathForAssets } from '@/utils/base-path';
import {
    CompetitionProgramBackgrounds,
    fallbackCompetitionProgramBackgrounds,
} from '@/utils/competition-images';
import { getMenuItems } from '@/utils/supabase/menu-items';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

type Festival = TicketingFestival;

interface FestivalContextValue {
    slug: string;
    basePath: string;
    festival: Festival | null;
    menuItems: FrontendMenuItem[];
    menuLoading: boolean;
    menuLoaded: boolean;
    menuError: string | null;
}

const FestivalContext = createContext<FestivalContextValue | null>(null);

interface FestivalProviderProps {
    slug: string;
    locale: string;
    festival: Festival | null;
    programBackgrounds?: CompetitionProgramBackgrounds;
    /** Skip the early-bird menu trim (admins testing an early-bird festival). */
    showFullMenu?: boolean;
    children: ReactNode;
}

export const FestivalProvider = ({
    slug,
    locale,
    festival,
    programBackgrounds,
    showFullMenu = false,
    children,
}: FestivalProviderProps) => {
    const basePath = normalizeBasePath(slug);
    const trimMenu = isEarlyBirdOnly(festival) && !showFullMenu;
    const [menuState, setMenuState] = useState<MenuState>({
        items: [],
        loading: !!festival?.id,
        loaded: false,
        error: null,
    });

    // Fetch menu items when festival changes
    useEffect(() => {
        const fetchMenuItems = async () => {
            if (!festival?.id) {
                setMenuState({ items: [], loading: false, loaded: true, error: null });
                return;
            }

            setMenuState((prev) => ({ ...prev, loading: true, error: null }));

            try {
                const items = await getMenuItems(festival.id, locale);
                setMenuState({
                    items: trimMenu ? filterEarlyBirdMenu(items, locale) : items,
                    loading: false,
                    loaded: true,
                    error: null,
                });
            } catch (error) {
                console.error('Error fetching menu items:', error);
                setMenuState({
                    items: [],
                    loading: false,
                    loaded: true,
                    error: 'Failed to load menu items',
                });
            }
        };

        fetchMenuItems();
    }, [festival?.id, locale, trimMenu]);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        document.documentElement.style.setProperty('--asset-prefix', basePath || '');
        document.documentElement.setAttribute('data-festival-slug', slug);

        const programUrls = programBackgrounds ?? fallbackCompetitionProgramBackgrounds(basePath);
        const entries: Array<[string, string]> = [
            [
                '--bg-coming-soon-en',
                withBasePathForAssets('/img/coming-soon/placeholder-en.svg', basePath),
            ],
            [
                '--bg-coming-soon-fr',
                withBasePathForAssets('/img/coming-soon/placeholder-fr.svg', basePath),
            ],
            ['--bg-not-found', withBasePathForAssets('/img/404.png', basePath)],
            ['--bg-program-features', programUrls.features],
            ['--bg-program-shorts', programUrls.shorts],
            ['--bg-program-documentary', programUrls.documentary],
        ];

        entries.forEach(([name, url]) => {
            const escapedUrl = url.replace(/'/g, "\\'");
            document.documentElement.style.setProperty(name, `url('${escapedUrl}')`);
        });
    }, [basePath, slug, programBackgrounds]);

    const value = useMemo<FestivalContextValue>(
        () => ({
            slug,
            basePath,
            festival,
            menuItems: menuState.items,
            menuLoading: menuState.loading,
            menuLoaded: menuState.loaded,
            menuError: menuState.error,
        }),
        [slug, basePath, festival, menuState],
    );

    return <FestivalContext.Provider value={value}>{children}</FestivalContext.Provider>;
};

export const useFestivalContext = () => {
    const context = useContext(FestivalContext);
    if (!context) {
        throw new Error('useFestivalContext must be used within a FestivalProvider');
    }
    return context;
};
